import * as React from 'react';
import axios from 'axios';
import { JobExecution, ListExecutionsResponseBody } from '@lct-jobs/core';
import { apiUrl } from 'utils/apiUrl';
import { useAuthContext } from 'contexts/AuthContext';

/**
 * Where an execution was listed from. lct-jobs keeps executions per tenant: the global tenant's
 * and each region's Regional tenant's lists hold mass jobs, batch runs and mass queries, and the
 * region list (lct-jobs ListRegionExecutions) holds every top-level execution in the region,
 * including jobs run on a single site.
 */
export type ExecutionSource = 'global' | 'regional' | 'sites';

export const EXECUTION_SOURCES: ExecutionSource[] = ['global', 'regional', 'sites'];

export type SourcedExecution = JobExecution & {
  source: ExecutionSource;
  /** The region whose list the row came from */
  sourceRegion: string;
};

/**
 * One list to page through: Global once, and each region's Regional list and region list. With
 * several regions chosen (All regions), every region adds its own two lists.
 */
interface ListSpec {
  key: string;
  source: ExecutionSource;
  region: string;
}

/**
 * The lists to read. With a tenant type chosen, only the region list can filter by it on the
 * server, and the Global and Regional lists only hold their own tenant type, so they are only
 * read when that is the type chosen.
 */
const listsFor = (regions: string[], tenantType?: string): ListSpec[] => [
  // The global tenant's executions are the same whichever region is asked
  ...(!tenantType || tenantType === 'Global'
    ? [{ key: 'global', source: 'global' as const, region: regions[0] }]
    : []),
  ...regions.flatMap((region) => [
    ...(!tenantType || tenantType === 'Regional'
      ? [{ key: `regional:${region}`, source: 'regional' as const, region }]
      : []),
    { key: `sites:${region}`, source: 'sites' as const, region },
  ]),
];

/** Rows asked for per list and page. The region list allows up to 200. */
export const PAGE_LIMIT = 50;

const DAY_MS = 24 * 60 * 60 * 1000;

const sourceUrl = (source: ExecutionSource, region: string) => {
  switch (source) {
    case 'global':
      return apiUrl('lct', 'tenants/global/jobManager/executions');
    case 'regional':
      return apiUrl('lct', `regions/${region}/tenantTypes/Regional/jobManager/executions`);
    default:
      return apiUrl('lct', `regions/${region}/jobManager/executions`);
  }
};

// A queued execution has no start date yet, so it sorts to the top as the newest
const startedAt = (execution: JobExecution) => {
  const time = execution.startDate ? new Date(execution.startDate).getTime() : NaN;
  return Number.isNaN(time) ? Infinity : time;
};

const byStartDateDesc = (a: JobExecution, b: JobExecution) => {
  const aStarted = startedAt(a);
  const bStarted = startedAt(b);
  return aStarted === bStarted ? a.executionId.localeCompare(b.executionId) : bStarted - aStarted;
};

/** The oldest start date loaded from a list, or undefined when it has no dated rows yet. */
const oldestStart = (executions: JobExecution[]) => {
  const times = executions.map(startedAt).filter(Number.isFinite);
  return times.length > 0 ? Math.min(...times) : undefined;
};

interface SourceState {
  executions: SourcedExecution[];
  nextPageToken?: string;
  answered: boolean;
}

const EMPTY_LIST: SourceState = { executions: [], answered: false };

// Rows from the region lists come first, so Global and Regional rows the region list also holds
// keep their own source (and detail page)
const SOURCE_ORDER: ExecutionSource[] = ['sites', 'global', 'regional'];

export interface Executions {
  /** Rows that can be placed in order, newest first. */
  executions: SourcedExecution[];
  loading: boolean;
  error?: Error;
  hasMore: boolean;
  loadMore: () => void;
  refresh: () => void;
}

/**
 * Global, Regional and region-list executions for one or more regions within the last `days`, newest first,
 * paged together. With a job, the region list is asked for that job's runs only (lct-jobs filters
 * it); the Global and Regional lists cannot filter, so callers still filter those rows themselves.
 *
 * Each list pages on its own, so after a load one list may reach back further than another. A row
 * is only shown once every list that still has pages has loaded back to its start date; older rows
 * are held back until then, so nothing unloaded can belong above a row already shown.
 */
export const useExecutions = (
  regions: string[],
  days: number,
  jobId?: string,
  tenantType?: string,
): Executions => {
  const { idToken } = useAuthContext();
  // Read at request time, so a token refresh does not throw away what is loaded
  const idTokenRef = React.useRef(idToken);
  idTokenRef.current = idToken;

  const regionsKey = regions.join(',');
  const lists = React.useMemo(
    () => listsFor(regionsKey.split(','), tenantType),
    [regionsKey, tenantType],
  );
  const [state, setState] = React.useState<Record<string, SourceState>>({});
  const listState = React.useCallback((key: string) => state[key] ?? EMPTY_LIST, [state]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | undefined>(undefined);
  const generationRef = React.useRef(0);

  // Fixed per load so paging and filtering agree on where the lookback ends
  const [since, setSince] = React.useState(() => Date.now() - days * DAY_MS);

  const fetchSources = React.useCallback(
    async (requests: { list: ListSpec; nextPageToken?: string }[]) => {
      const generation = generationRef.current;
      const headers = { Authorization: `Bearer ${idTokenRef.current}` };
      setLoading(true);
      const responses = await Promise.allSettled(
        requests.map(({ list, nextPageToken }) =>
          axios.get<ListExecutionsResponseBody>(sourceUrl(list.source, list.region), {
            headers,
            params: {
              limit: PAGE_LIMIT,
              ...(nextPageToken ? { nextPageToken } : {}),
              // The region list filters on the server; a chosen tenant type saves reading every
              // tenant type's partition (lct-jobs#437)
              ...(list.source === 'sites'
                ? { days, ...(jobId ? { jobId } : {}), ...(tenantType ? { tenantType } : {}) }
                : {}),
            },
          }),
        ),
      );
      if (generation !== generationRef.current) return;

      setState((prev) => {
        const next = { ...prev };
        requests.forEach(({ list }, index) => {
          const response = responses[index];
          const previous = prev[list.key] ?? EMPTY_LIST;
          if (response.status !== 'fulfilled') {
            // A failed list stops paging rather than holding every other list's rows back
            next[list.key] = { ...previous, nextPageToken: undefined, answered: true };
            return;
          }
          const incoming = (response.value.data?.executions ?? []).map((execution) => ({
            ...execution,
            source: list.source,
            sourceRegion: list.region,
          }));
          // Later pages win per execution id
          const byId = new Map(previous.executions.map((e) => [e.executionId, e]));
          incoming.forEach((execution) => byId.set(execution.executionId, execution));
          next[list.key] = {
            executions: Array.from(byId.values()),
            nextPageToken: response.value.data?.nextPageToken,
            answered: true,
          };
        });
        return next;
      });
      // One list failing (for example no permission for Regional) still shows the others, and
      // the error is shown with them
      const failed = responses.find(
        (response): response is PromiseRejectedResult => response.status === 'rejected',
      );
      setError(failed ? (failed.reason as Error) : undefined);
      setLoading(false);
    },
    [days, jobId, tenantType],
  );

  const refresh = React.useCallback(() => {
    generationRef.current += 1;
    setState({});
    setError(undefined);
    setSince(Date.now() - days * DAY_MS);
    fetchSources(lists.map((list) => ({ list })));
  }, [fetchSources, days, lists]);

  React.useEffect(() => {
    refresh();
    return () => {
      generationRef.current += 1;
    };
  }, [refresh]);

  // Global and Regional cannot be asked for a date range, so they stop paging once they reach
  // back past the lookback
  const open = React.useMemo(
    () =>
      lists.filter(({ key }) => {
        const { executions, nextPageToken } = listState(key);
        if (!nextPageToken) return false;
        const oldest = oldestStart(executions);
        return oldest === undefined || oldest >= since;
      }),
    [lists, listState, since],
  );

  const loadMore = React.useCallback(() => {
    if (loading || open.length === 0) return;
    fetchSources(open.map((list) => ({ list, nextPageToken: listState(list.key).nextPageToken })));
  }, [loading, open, listState, fetchSources]);

  const executions = React.useMemo(() => {
    // The newest of the oldest rows of the lists still paging: below it, a list that has not
    // loaded that far could still have rows to slot in. A list with no dated rows yet tells us
    // nothing about where its rows fall, so it does not hold the others back.
    const cutoff = Math.max(
      since,
      ...open.map(({ key }) => oldestStart(listState(key).executions) ?? -Infinity),
    );

    // The region list also holds the Global and Regional executions of its region; those keep
    // their own source so they link to the same detail pages as before
    const byId = new Map<string, SourcedExecution>();
    SOURCE_ORDER.forEach((source) =>
      lists
        .filter((list) => list.source === source)
        .forEach(({ key }) =>
          listState(key).executions.forEach((execution) =>
            byId.set(execution.executionId, execution),
          ),
        ),
    );
    return Array.from(byId.values())
      .filter((execution) => startedAt(execution) >= cutoff)
      .sort(byStartDateDesc);
  }, [lists, listState, since, open]);

  return {
    executions,
    loading,
    error,
    hasMore: open.length > 0,
    loadMore,
    refresh,
  };
};
